import React, { useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { getPickupHistory, saveOfficePickup } from "../../../services/tripTrackingApi";
import { formatDateTimeDMY12 } from "../../../utils/dateFormat";

// "12.9716, 77.5946" typed straight in, rather than a Google Maps link.
const COORDINATES = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

/**
 * The pickup spot the customer chose on their trip page, under the policy status in Manage
 * Bookings. Until it is set, an open booking shows a warning; the office can set or correct the
 * spot and see every change. The pickup time itself is edited on the booking as before.
 */
export default function BookingPickupStatus({ booking, onPatched }) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(null);
  const point = booking.pickupPoint;
  const open = !["CANCELLED", "COMPLETED"].includes(booking.tripStatus);

  const showHistory = async () => {
    setHistory([]);
    try {
      setHistory(await getPickupHistory(booking.id));
    } catch (error) {
      setHistory(null);
      alert(error?.response?.data?.message || "Could not load the pickup changes.");
    }
  };

  return (
    <div className="small">
      {point ? (
        <>
          <Badge bg={point.updatedBy === "CUSTOMER" ? "success" : "info"}>
            Pickup set by {point.updatedBy === "CUSTOMER" ? "customer" : "office"}
          </Badge>
          <div className="mt-1 text-wrap" style={{ maxWidth: 220, wordBreak: "break-word" }}>{point.landmark}</div>
          {point.contactPhone ? <div>Call: <a href={`tel:${point.contactPhone}`}>{point.contactPhone}</a></div> : null}
          {point.timeMovedByCustomer ? (
            <div className="text-warning-emphasis fw-semibold">
              Time moved by customer: {formatDateTimeDMY12(point.earliestPickupAt, "-")} →{" "}
              {formatDateTimeDMY12(booking.raw?.pickupDateTime ?? booking.pickupDateTime, "-")}
            </div>
          ) : null}
          <div className="text-muted">Updated {formatDateTimeDMY12(point.updatedAt, "-")}</div>
          <div className="d-flex gap-2 mt-1">
            <a href={point.mapLink} target="_blank" rel="noopener noreferrer">Map</a>
            <Button variant="link" size="sm" className="p-0 small" onClick={() => setEditing(true)}>Edit</Button>
            <Button variant="link" size="sm" className="p-0 small" onClick={showHistory}>History</Button>
          </div>
        </>
      ) : open ? (
        <>
          <Badge bg="warning" text="dark">Pickup not set</Badge>
          <div>
            <Button variant="link" size="sm" className="p-0 small" onClick={() => setEditing(true)}>Set for customer</Button>
          </div>
        </>
      ) : null}

      <OfficePickupModal
        show={editing}
        booking={booking}
        onHide={() => setEditing(false)}
        onSaved={(view) => {
          setEditing(false);
          onPatched({ id: booking.id, pickupPoint: view });
        }}
      />
      <Modal show={history !== null} onHide={() => setHistory(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Pickup changes · Booking #{booking.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {history && history.length ? history.map((change, index) => (
            <div key={`${change.changedAt}-${index}`} className={index ? "border-top pt-2 mt-2" : ""}>
              <div className="fw-semibold">
                {formatDateTimeDMY12(change.changedAt, "-")} · {change.changedByName || change.changedBy}
              </div>
              <div>Pickup time: {formatDateTimeDMY12(change.pickupAt, "-")}</div>
              <div className="text-wrap">{change.landmark}</div>
              {change.contactPhone ? <div>Call: {change.contactPhone}</div> : null}
              <a href={`https://www.google.com/maps/search/?api=1&query=${change.latitude},${change.longitude}`} target="_blank" rel="noopener noreferrer">
                Map
              </a>
            </div>
          )) : <span className="text-muted">No changes yet.</span>}
        </Modal.Body>
      </Modal>
    </div>
  );
}

function OfficePickupModal({ show, booking, onHide, onSaved }) {
  const point = booking.pickupPoint;
  const [where, setWhere] = useState("");
  const [landmark, setLandmark] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setWhere(point ? `${point.latitude}, ${point.longitude}` : "");
    setLandmark(point?.landmark || "");
    setPhone(point?.contactPhone || "");
  };

  const save = async () => {
    if (!where.trim() || !landmark.trim()) {
      alert("Enter the location and a landmark.");
      return;
    }
    const match = where.match(COORDINATES);
    setSaving(true);
    try {
      const view = await saveOfficePickup(booking.id, {
        latitude: match ? Number(match[1]) : null,
        longitude: match ? Number(match[2]) : null,
        link: match ? null : where.trim(),
        landmark: landmark.trim(),
        contactPhone: phone.trim() || null,
      });
      onSaved(view);
    } catch (error) {
      alert(error?.response?.data?.message || "Could not save the pickup point.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} onShow={reset} centered>
      <Modal.Header closeButton>
        <Modal.Title className="h6">Pickup point · Booking #{booking.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Google Maps link, or "latitude, longitude"</Form.Label>
          <Form.Control value={where} onChange={(event) => setWhere(event.target.value)} placeholder="https://maps.app.goo.gl/..." />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Landmark / house details for the driver</Form.Label>
          <Form.Control as="textarea" rows={2} maxLength={300} value={landmark} onChange={(event) => setLandmark(event.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label className="small fw-semibold">Phone to call at pickup (optional)</Form.Label>
          <Form.Control value={phone} onChange={(event) => setPhone(event.target.value)} />
        </Form.Group>
        <div className="small text-muted mt-2">The pickup time is changed in the booking's Vehicle pickup date/time, as before.</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
